import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, UserCheck, FileText } from 'lucide-react';

const VIUBUX_LOGO = 'https://customer-assets.emergentagent.com/job_libro-quote-gen/artifacts/pf9umyn8_5.png';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f133a] to-[#1a1f50]">
      {/* Header */}
      <header className="bg-[#0f133a]/90 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <img 
                src={VIUBUX_LOGO} 
                alt="Viubux" 
                className="h-8 w-auto"
              />
              <span className="text-white font-bold text-lg">Viubux</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Volver al Cotizador</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
          {/* Title Section */}
          <div className="bg-gradient-to-r from-[#aa2aff] to-[#272b96] p-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Política de Privacidad
                </h1>
                <p className="text-white/80 mt-1">
                  Última actualización: {new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </div>
          </div>

          {/* Policy Content */}
          <div className="p-8 space-y-8 text-slate-300">
            {/* Introduction */}
            <section>
              <p className="text-lg leading-relaxed">
                En <span className="text-white font-semibold">Viubux</span>, nos comprometemos a proteger tu privacidad 
                y tus datos personales. Esta Política de Privacidad describe cómo recopilamos, utilizamos, 
                almacenamos y protegemos tu información personal de conformidad con la 
                <span className="text-[#aa2aff]"> Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP)</span> 
                y demás disposiciones aplicables en México.
              </p>
            </section>

            {/* Section 1 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#aa2aff]/20 rounded-lg">
                  <FileText className="w-5 h-5 text-[#aa2aff]" />
                </div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  1. Información que Recopilamos
                </h2>
              </div>
              <div className="pl-11 space-y-3">
                <p>Para brindarte nuestros servicios de cotización de producción audiovisual, podemos recopilar:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Nombre completo</li>
                  <li>Correo electrónico</li>
                  <li>Número telefónico</li>
                  <li>Información sobre tu proyecto (título del libro, nombre del autor, etc.)</li>
                  <li>Documentos fiscales (Constancia de Situación Fiscal, cuando aplique)</li>
                </ul>
              </div>
            </section>

            {/* Section 2 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#aa2aff]/20 rounded-lg">
                  <Eye className="w-5 h-5 text-[#aa2aff]" />
                </div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  2. Uso de la Información
                </h2>
              </div>
              <div className="pl-11 space-y-3">
                <p>Tu información personal será utilizada exclusivamente para:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Generar y enviar cotizaciones personalizadas</li>
                  <li>Contactarte en relación a tu proyecto</li>
                  <li>Procesar solicitudes de producción</li>
                  <li>Cumplir con obligaciones fiscales y legales</li>
                </ul>
                <div className="bg-[#aa2aff]/10 border border-[#aa2aff]/30 rounded-lg p-4 mt-4">
                  <p className="text-white font-medium">
                    Tu información NO será utilizada para fines distintos a los aquí establecidos.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 3 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#aa2aff]/20 rounded-lg">
                  <Lock className="w-5 h-5 text-[#aa2aff]" />
                </div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  3. Protección de Datos
                </h2>
              </div>
              <div className="pl-11 space-y-3">
                <p>Implementamos medidas de seguridad administrativas, técnicas y físicas para proteger tu información personal:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Encriptación de datos en tránsito y en reposo</li>
                  <li>Acceso restringido solo a personal autorizado</li>
                  <li>Sistemas de seguridad actualizados</li>
                  <li>Copias de seguridad periódicas</li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#aa2aff]/20 rounded-lg">
                  <UserCheck className="w-5 h-5 text-[#aa2aff]" />
                </div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  4. No Compartimos tu Información
                </h2>
              </div>
              <div className="pl-11">
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg p-6">
                  <p className="text-white text-lg font-medium mb-4">
                    Nos comprometemos a que tu información personal:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <span className="text-green-400 text-xl">✓</span>
                      <span><strong className="text-white">NO será vendida</strong> a terceros</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-400 text-xl">✓</span>
                      <span><strong className="text-white">NO será compartida</strong> con fines comerciales</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-400 text-xl">✓</span>
                      <span><strong className="text-white">NO será rentada</strong> a ninguna entidad</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="text-green-400 text-xl">✓</span>
                      <span><strong className="text-white">NO será regalada</strong> a terceros</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 5 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#aa2aff]/20 rounded-lg">
                  <Shield className="w-5 h-5 text-[#aa2aff]" />
                </div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  5. Tus Derechos ARCO
                </h2>
              </div>
              <div className="pl-11 space-y-3">
                <p>De acuerdo con la LFPDPPP, tienes derecho a:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong className="text-white">Acceso:</strong> Conocer qué datos personales tenemos sobre ti</li>
                  <li><strong className="text-white">Rectificación:</strong> Solicitar la corrección de tus datos</li>
                  <li><strong className="text-white">Cancelación:</strong> Solicitar la eliminación de tus datos</li>
                  <li><strong className="text-white">Oposición:</strong> Oponerte al tratamiento de tus datos</li>
                </ul>
                <p className="mt-4">
                  Para ejercer estos derechos, contáctanos a: 
                  <a href="mailto:privacidad@viubux.com" className="text-[#aa2aff] hover:underline ml-1">
                    privacidad@viubux.com
                  </a>
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-[#0f133a] rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-bold text-white mb-3">Contacto</h3>
              <p className="text-slate-400 mb-4">
                Si tienes preguntas sobre esta Política de Privacidad, puedes contactarnos:
              </p>
              <div className="space-y-2 text-slate-300">
                <p>Email: <a href="mailto:privacidad@viubux.com" className="text-[#aa2aff]">privacidad@viubux.com</a></p>
                <p>Teléfono: <a href="tel:+525548564696" className="text-[#aa2aff]">+52 55 4856 4696</a></p>
                <p>Sitio web: <a href="https://www.viubux.com" target="_blank" rel="noopener noreferrer" className="text-[#aa2aff]">www.viubux.com</a></p>
              </div>
            </section>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center mt-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Volver al Cotizador de Servicios</span>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#0f133a]/80 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Viubux. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
